import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { DateTime } from 'luxon'

import {
  Badge,
  BooleanRadioGroup,
  Breadcrumb,
  Button,
  Card,
  CardColors,
  Icon,
  Image,
  Separator,
  Text,
} from '@/ui'
import {
  faArrowLeft,
  faBullseye,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-regular-svg-icons'
import { TypographyStyles } from '@/styles'
import { useReselector } from '@/utils/sharedHooks'
import { getFinancialProfile } from '@/selectors/user.selectors'
import {
  formatCurrency,
  formatCurrencyFromCents,
} from '@/utils/currencyHelpers'
import { selectProfitGoal } from '@/features/goal-setting/goals.selectors'
import {
  fetchUserIncomeMonthsForYear,
  selectIncomePlanningPlannedIncomeForYear,
} from '@/features/income-planning/incomePlanning.slice'
import { useAppDispatch } from '@/utils/typeHelpers'
import { getProfitGoal } from '@/features/goal-setting/helpers'
import { updateFinancialProfile } from '@/actions/financialProfileActions'

export const IncomePlanningUpdateGoal = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const currentDate = useMemo(() => DateTime.now(), [])
  const [shouldUpdateValue, setShouldUpdateValue] = useState<boolean>()

  const fp = useReselector(getFinancialProfile)
  const profitGoal = useReselector(selectProfitGoal)

  const plannedIncomeRollupForYear = useReselector(
    selectIncomePlanningPlannedIncomeForYear,
    currentDate.year
  )

  const updateGoal = useCallback(async () => {
    if (shouldUpdateValue && plannedIncomeRollupForYear) {
      await dispatch(
        updateFinancialProfile(fp?.id, {
          annualIncomeGoalInCents: plannedIncomeRollupForYear.intValue,
        })
      )
    }

    navigate('/income-planning')
  }, [
    dispatch,
    fp?.id,
    navigate,
    plannedIncomeRollupForYear,
    shouldUpdateValue,
  ])

  const percentDifference =
    fp?.annualIncomeGoalInCents && plannedIncomeRollupForYear?.intValue
      ? ((plannedIncomeRollupForYear.intValue - fp.annualIncomeGoalInCents) /
          fp.annualIncomeGoalInCents) *
        100
      : 0

  useEffect(() => {
    dispatch(fetchUserIncomeMonthsForYear(currentDate.year))
  }, [currentDate.year, dispatch])

  return (
    <>
      <Breadcrumb>
        <Breadcrumb.List>
          <Breadcrumb.Item>
            <Breadcrumb.Link onClick={() => navigate('/income-planning')}>
              <Icon icon={faArrowLeft} />
              Back to Income Planning
            </Breadcrumb.Link>
          </Breadcrumb.Item>
        </Breadcrumb.List>
      </Breadcrumb>

      <div className="mt-10 md:px-10 lg:px-10">
        <Image
          src="https://heard-images.s3.amazonaws.com/assets/progress.svg"
          alt="progress"
          className="mt-10"
          width={80}
        />
        <Text variant={TypographyStyles.DISPLAY_MD} className="mt-4">
          Update Goals
        </Text>
        <Text variant={TypographyStyles.BODY_SM} className="mt-4">
          Below are your current goals, and what your new goals would be with
          your planned income.
        </Text>
        <Card color={CardColors.LEVEL_1} className="mt-10">
          <div className="flex">
            <Icon icon={faBullseye} color="text-moss-200" />
            <Text variant={TypographyStyles.LABEL_MD}>
              Your Current Annual Goals
            </Text>
          </div>
          <div className="flex mt-6">
            <div className="w-1/3">
              <Text variant={TypographyStyles.LABEL_SM}>Income</Text>
              <Text variant={TypographyStyles.DISPLAY_XS} className="mt-2">
                {formatCurrencyFromCents(fp?.annualIncomeGoalInCents, {
                  hideDecimalsIfZero: true,
                })}
              </Text>
            </div>
            <div className="w-1/3">
              <Text variant={TypographyStyles.LABEL_SM}>Expense Ratio</Text>
              <Text variant={TypographyStyles.DISPLAY_XS} className="mt-2">
                {fp?.annualExpenseRatioGoal
                  ? fp?.annualExpenseRatioGoal * 100
                  : '-'}
                %
              </Text>
            </div>
            <div className="w-1/3">
              <Text variant={TypographyStyles.LABEL_SM}>Profit</Text>
              <Text variant={TypographyStyles.DISPLAY_XS} className="mt-2">
                {formatCurrency(profitGoal, { hideDecimalsIfZero: true })}
              </Text>
            </div>
          </div>
          <Button
            onClick={() => navigate('/goal-setting')}
            className="mt-6 pl-0"
            variant="link"
          >
            View annual goals →
          </Button>
        </Card>

        <Card color={CardColors.EMPHASIS} className="mt-6">
          <div className="flex">
            <Icon icon={faBullseye} color="text-moss-200" />
            <Text variant={TypographyStyles.LABEL_MD}>
              Your New Annual Goals
            </Text>
          </div>
          <Text className="mt-2">
            These updated goals are based upon the monthly planned income that
            you just calculated.
          </Text>
          <div className="flex mt-6">
            <div className="w-1/3">
              <Text variant={TypographyStyles.LABEL_SM}>Income</Text>
              <div className="flex">
                <Text variant={TypographyStyles.DISPLAY_XS} className="mt-2">
                  {formatCurrency(plannedIncomeRollupForYear, {
                    hideDecimalsIfZero: true,
                  })}
                </Text>
                {Boolean(percentDifference) && (
                  <Badge color="green" variant="secondary" className="ml-2">
                    {percentDifference > 0 ? '+' : ''}
                    {percentDifference.toFixed(2)}%
                  </Badge>
                )}
              </div>
            </div>
            <div className="w-1/3">
              <Text variant={TypographyStyles.LABEL_SM}>Expense Ratio</Text>
              <Text variant={TypographyStyles.DISPLAY_XS} className="mt-2">
                {fp?.annualExpenseRatioGoal
                  ? fp?.annualExpenseRatioGoal * 100
                  : '-'}
                %
              </Text>
            </div>
            <div className="w-1/3">
              <Text variant={TypographyStyles.LABEL_SM}>Profit</Text>
              <Text variant={TypographyStyles.DISPLAY_XS} className="mt-2">
                {formatCurrency(
                  getProfitGoal(
                    plannedIncomeRollupForYear?.intValue,
                    fp?.annualExpenseRatioGoal
                  ),
                  { hideDecimalsIfZero: true }
                )}
              </Text>
            </div>
          </div>
        </Card>

        <BooleanRadioGroup
          legend="Would you like us to update your annual goals?"
          yesLabel="Yes, update my annual goals with my new planned income"
          noLabel="No, keep my annual goals the same"
          value={shouldUpdateValue}
          onValueChange={setShouldUpdateValue}
          className="mt-6"
        />

        <Separator className="mt-10" />
        <div className="w-full flex justify-between mt-6">
          <Button
            onClick={() => navigate('/income-planning')}
            variant="secondary"
            iconLeft={faChevronLeft}
          >
            Back
          </Button>
          <div className="flex gap-2">
            <Button
              onClick={updateGoal}
              iconRight={faChevronRight}
              disabled={shouldUpdateValue === undefined}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}
